import Btn from 'components/buttons/Btn'
import { BtnDesc } from 'components/form'
import I from 'components/I'
import Icon from 'components/Icon'
import Screen from 'components/layout/Screen'
import PropTypes from 'prop-types'
import { Card, ListGroup } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useAccount } from 'state/Account'
import { useUser } from 'state/Auth'
import { PRIVILEGES } from 'state/Constants'
import Order from 'state/models/Order'
import { getHasReseller, getIsCMO, useReseller } from 'state/Reseller'
import { getUpdatesVersion, setUpdatesVersion } from 'state/Settings'
import { klass, useCleanup } from 'utils'
import './styles.scss'

/**
 * @param t {function|TFunction|undefined}
 * @param user {User}
 * @param account {Account}
 * @param reseller {Reseller}
 * @param hasReseller {boolean}
 * @param isCMO {?boolean}
 * @return {Object<number,JSX.Element>|number[]} Each entry can be a {@link Card.Body} or {@link ListGroup} (or maybe other similar elements)
 * If the first param is empty, returns the latest version number instead :))
 * Note for future maintenance:
 *  - use Hemingway, so the text is more concise and clear: http://www.hemingwayapp.com (web version is enough)
 *  - more fixes can be done by passing the text through https://languagetool.org (useful even for paraphrasing)
 *  - include buttons at the footer of the card whenever there's a new page, so the user is drawn to visit it
 *  - dates are just month/year because we're not sure when it's going to be merged and deployed ¯\_(ツ)_/¯
 */
const ENTRIES = (t, user, account, reseller, hasReseller = false, isCMO = null) => {
	const LATEST   = 12
	const CMO_ONLY = 2
	if (!account) {
		return [LATEST, CMO_ONLY]
	}

	const allowedAdjust = user.usePrivilege(PRIVILEGES().ORDER)

	//eslint-disable i18next/no-literal-string
	//TODO recycle entries older than 2 years from now (e.g. in 2024, keep all entries from 2022 and 2023)
	//HEY, YOU! DON'T FORGET TO UPDATE `CMO_ONLY` IF YOU ADD FURTHER ENTRIES SPECIFIC TO CMO!!
	return {
		[LATEST]: <ListGroup.Item>
			<Card.Title>📝 Adjust order items & tips</Card.Title>
			<Card.Subtitle>February 2025</Card.Subtitle>
			<p>{/* sc40275 */} <Version n="2.23.0"/> The customer forgot to order <I force>that</I> add-on, and called asking for it? Sure! Now you are able to include it in the order - and, optionally, charge for it as well.</p>
			<p>It's also possible to adjust the tip value shown in the order.</p>
			<p>If the order was paid online, it will be automatically adjusted.</p>
			<p>
				To adjust an order, {allowedAdjust?
					<>use the top-right menu on the Order Details screen, and go to <BtnDesc icon={['far', 'money-check-edit-alt']} noText/> Adjust payment/tip</>
				: 'call the Support team'
				}. Refunds can {allowedAdjust? '' : 'also'} be made by the Support.
			</p>
		</ListGroup.Item>
		,
		11: <ListGroup variant="flush">
			<ListGroup.Item>
				<Card.Title>⏱️Temporarily turn off order types</Card.Title>
				<Card.Subtitle>May 2024</Card.Subtitle>
				<p>{/* sc40275 */} <Version n="2.22.0"/> We've just revamped and expanded how you can control your ordering flow.</p>
				<p>
					With this update, you can disable order types one by one, instead of either opening or closing the entire {account.location_wording_singular}.<br/>
					For instance, consider a large birthday group suddenly showing up at your place: you can now pause
					online dine-in orders for a couple of hours. And if they leave before that, just re-enable it early.
				</p>
				<p>
					As usual, changing all order types at once is still possible, whether for closing them for the same period or reopening ahead of time. The option stays at the bottom of the sidebar. It's also still possible to early reopen from an alert at the top of the screen, when any order type is off.
				</p>
			</ListGroup.Item>
			<Card.Footer>
				<Btn as={Link} to="/status" swap icon="power-off">See current ordering status</Btn>
			</Card.Footer>
		</ListGroup>
		,
		10: isCMO && <ListGroup variant="flush">
			<ListGroup.Item>
				<Card.Title>🚗 Auto-dispatch deliveries</Card.Title>
				<Card.Subtitle>April 2024</Card.Subtitle>
				<p>
					{/* sc51830 */} <Version n="2.21.0"/> It is now possible to automatically dispatch third-party deliveries as soon as the orders are placed by customers.<br/>
					You can turn it on it under <NavPath parts={[['cog', 'Settings'], ['car-side', 'Delivery services'], ['toggle-on', 'Auto-dispatching']]}/>.
				</p>
				<p>
					We've also added an <b>in-house</b> delivery button: it can be used to remove an order from the auto-dispatch queue, avoiding fees if you choose to handle it with your own drivers. Note this will not show up when auto-dispatch is not enabled.
					</p>
			</ListGroup.Item>
			<ListGroup.Item>
				<b>Minor changes</b>
				<ul>
					<li>{/* sc53194 */} <Version n="2.21.3"/> Before June, "in-house delivery" was known as "deliver by ourselves" in the button and texts.</li>
				</ul>
			</ListGroup.Item>
			<Card.Footer>
			<Btn as={Link} to="/settings#delivery" swap icon="car-side">Check delivery settings</Btn>
			</Card.Footer>
		</ListGroup>
		,
		9: isCMO && <ListGroup variant="flush">
			<ListGroup.Item>
				<Trans>
					<Card.Title>🚙 Third-party deliveries</Card.Title>
					<Card.Subtitle>February 2024</Card.Subtitle>
					<p>
						{/* sc46584 */}<Version n="2.20.0"/> We've recently added a delivery feature for your restaurant's orders, handled by trusted third-party companies.
					</p>
					<p>
						To activate, go to Settings and request it. Our Support Team will assist and enable it for you. Once enabled, orders within the configured range display a "Request driver" button for quick third-party delivery. Track the driver's journey while focusing on cooking and packing – they'll handle the rest.
					</p>
				</Trans>
			</ListGroup.Item>
			<Card.Footer>
				<Btn as={Link} to="/settings#delivery" swap icon="car-side"><Trans>Check delivery settings</Trans></Btn>
			</Card.Footer>
		</ListGroup>
		,
		8: <ListGroup variant="flush">
			<ListGroup.Item>
				<Card.Title>🐞 Fixes & improvements</Card.Title>
				<Card.Subtitle>2023</Card.Subtitle>
				<strong>Minor changes</strong>
				<ul>
					<li><Version n="2.18.2 ~ 2.18.13"/> Several releases of improvements and fixes in different sections of our app.</li>
					<li>
						{/* sc46933 */}<Version n="2.18.14"/> The Open Orders list is now grouped between "Today" and "Yesterday", with other days falling under "Earlier" - helpful if you prefer to keep all orders in the same tab, or sometimes forget to mark orders as completed.<br/>
					<small className="ml-3 d-block"><b>Quick tip:</b> if you don't mark orders as completed, you can disable that under <NavPath parts={[['cog', 'Settings'],['list', 'Orders List'],['toggle-on','Enable "Mark as Completed"']]}/>.</small>
					</li>
					<li>
						<Version n="2.18.18"/> Printer auto-search (Settings screen) got removed from iOS - it was already absent on Android. This improves performance and avoids unneeded permissions. Also, we fixed navigating back from an order after push notifications on iOS - among other minor improvements.
					</li>
				</ul>
			</ListGroup.Item>
		</ListGroup>
		,
		7: <ListGroup variant="flush">
			<ListGroup.Item>
				<Card.Title>💵 Item & price states</Card.Title>
				<Card.Subtitle>March 2023</Card.Subtitle>
				<p>{/* sc34800 */}<Version n="2.18"/> Prices now can be directly hidden. You can do this through the <BtnDesc icon="eye"/> besides each price in the Item Details (from the <BtnDesc icon="edit"/>). The <BtnDesc icon="trash"/> can be used to remove the price altogether.</p>
				<p>{/* sc34800 */} When an Item has no visible prices (and thus, can't be ordered), it will change automatically to hidden. Similarly, items without a category stay locked in their current state, since they have nowhere to show up.</p>
			</ListGroup.Item>
			<ListGroup.Item>
				<strong>Minor changes</strong>
				<ul>
					<li>{/* sc32483 */}<Version n="2.17.2"/> Describe add-on prices and quantities more clearly on the Order Details.</li>
					<li>{/* sc29012 */}<Version n="2.18.1"/> Suggest downloading the native app when available, or adding a mobile shortcut to the web app.</li>
				</ul>
			</ListGroup.Item>
			<Card.Footer>
				<Btn as={Link} to="/items" swap icon="book-open">See my Menu Items</Btn>
			</Card.Footer>
		</ListGroup>
		,
		6: <ListGroup variant="flush">
			<ListGroup.Item>
				<Card.Title>📸 Item pictures</Card.Title>
				<Card.Subtitle>October 2022</Card.Subtitle>
				<p>{/* sc21868 */}<Version n="2.17"/> Menu item photos can now be uploaded directly from the <Link to="/items">Menu list</Link>! You can check that with the <BtnDesc icon="camera"/>. There, you can select and crop a picture from your files or directly from the camera, if you're on a mobile device.</p>
			</ListGroup.Item>
		</ListGroup>
		,
		5: <ListGroup variant="flush">
			<ListGroup.Item>
				<Card.Title>🔢 Versions, hide category</Card.Title>
				<Card.Subtitle>June/July 2022</Card.Subtitle>
				<p>{/* sc35136 */}<Version n="2.14.1"/> This is a small change, but useful for tracking down possible issues: the app now has humanized version numbers. These are shown at the bottom of the <Link to="/settings">Settings</Link> screen. Always use it when reporting a problem to support!</p>
				<p>{/* sc35830 */}<Version n="2.15.0"/> It's now possible to hide the whole category at the <Link to="/items">Menu Items</Link> screen, instead of hiding item by item.</p>
			{reseller?.show_invoices && <p>{/* sc27541 */}<Version n="2.16.0"/> Besides checking your latest <Link to="/invoices">Invoices</Link>, it's now possible to pay them directly with credit card, if they're configured for online payment.</p>}
			</ListGroup.Item>
		</ListGroup>
		,
		4: <ListGroup variant="flush">
			<ListGroup.Item>
				<Card.Title>🌟 VIP Customers</Card.Title>
				<Card.Subtitle>May 2022</Card.Subtitle>
			<p>{/* sc33993 */} Do you have <I force>special</I> customers? Now you can mark them as VIP, so all their orders in the list will get a slight golden highlight. This way you won't forget to give them extra attention!</p>
				<p>You can use the <BtnDesc icon="star"/> besides the customer name to change that status, at the details of any order they have.</p>
			</ListGroup.Item>
		</ListGroup>
		,
		3: <ListGroup variant="flush">
			<ListGroup.Item>
				<Card.Title>
					📝 Unavailable Items
					{account.isMultiRestaurant && `, Order ${account.location_wording_plural}`}
					{hasReseller && ' & invoices'}
				</Card.Title>
				<Card.Subtitle>April 2022</Card.Subtitle>
				<p>{/* sc24120 */} Items now have three different states: they can be shown or hidden as usual, but now you can also set one as <em>unavailable</em>: it will show in the ordering menu, but the customer won't be able to place it in cart. It's useful for temporarily out-of-stock and other scenarios.</p>
			</ListGroup.Item>
			{account.isMultiRestaurant && <ListGroup.Item>
				<p>{/* sc34586 */} The {account.location_wording_singular} name now shows on the Orders List and Order Details. This helps reducing confusion when it's possible to view orders for other/all {account.location_wording_plural}.</p>
			</ListGroup.Item>}
			{hasReseller && <ListGroup.Item>
				<p>{/* sc22323 */} Last but not least, now it's possible to see your current and past invoices in the app.</p>
			</ListGroup.Item>}
			<ListGroup.Item>
				<strong>Minor changes</strong>
				<ul>
					<li>Improvements to editing Item Prices (more to come!)</li>
				</ul>
			</ListGroup.Item>
			{hasReseller && <Card.Footer>
				<Btn as={Link} to="/invoices" swap icon="file-invoice-dollar">Go to my Invoices</Btn>
			</Card.Footer>}
		</ListGroup>
		,
		2: <ListGroup variant="flush">
			<ListGroup.Item>
				<Card.Title>⏩️ Future Orders & font sizes</Card.Title>
				<Card.Subtitle>February 2022</Card.Subtitle>
				<p>{/* sc33749 */} The list showing what you'll need to prepare tomorrow now has a counter and is more consistent with the other lists. There's also a new setting that enables notifications for future orders.</p>
			</ListGroup.Item>
			<ListGroup.Item>
				<strong>Minor changes</strong>
				<ul>
					<li>{/* ch29825 */} iOS apps now respect the system Settings for Dynamic Font (text size). Details on how to configure that (for other platforms, too!) can be found in the Settings.</li>
				</ul>
			</ListGroup.Item>
			<Card.Footer>
				<Btn as={Link} to="/future" swap {...Order.ICONS[Order.STATUS.FUTURE]}>See my Future Orders</Btn> <Btn as={Link} to="/settings" swap icon="cog">Go to Settings</Btn>
			</Card.Footer>
		</ListGroup>
		,
		1: <ListGroup variant="flush">
			<ListGroup.Item>
				<Card.Title>📖️ Edit item details and prices</Card.Title>
				<Card.Subtitle>January 2022</Card.Subtitle>
				<p>{/* ch21870, ch27542 */} It's now possible to edit some details about your Menu Items, as well as their prices! You can use the <BtnDesc icon="pencil-alt"/> from the Menu Items list to make your last-minute changes.</p>
			</ListGroup.Item>
			<Card.Footer>
				<Btn as={Link} to="/items" swap icon="book-open">Check the Menu Items</Btn>
			</Card.Footer>
		</ListGroup>
		//eslint-enable i18next/no-literal-string
	}
}

const COUNTS = ENTRIES()
/**
 * Total number of updates so far, both for CMO (true) and non-CMO (false) accounts. The keys are unnamed so the CMO name doesn't leak to the compiled source (at least not here lol).
 * @type {{true: number, false: number}}
 */
UpdatesPage.LATEST_VERSION = {
	true:  COUNTS[0], //isCMO = true
	false: COUNTS[0] - COUNTS[1], //isCMO = false
}

export default function UpdatesPage() {
	const { t }       = useTranslation()
	const dispatch    = useDispatch()
	const account     = useAccount()
	const reseller    = useReseller()
	const user        = useUser()
	const hasReseller = useSelector(getHasReseller)
	const isCMO       = useSelector(getIsCMO)

	useCleanup(() => {
		dispatch(setUpdatesVersion(UpdatesPage.LATEST_VERSION[isCMO])) //bump current version when the user leaves
	}, [dispatch])

	return <Screen title={t('Latest Changes')}>
		<Screen.Content addPadding>
			{Object.entries(ENTRIES(t, user, account, reseller, hasReseller, isCMO))
				.filter(([_, e]) => e) //clean empty entries
				.sort((a, b) => a[1] > b[1]? 1 : -1) //places the latest at the top
				.map(([version, content]) => (
					<Entry key={version} version={parseInt(version)}>{content}</Entry>
			))}

			<div className="text-center mb-4 text-muted">
				<Icon i="ellipsis-v" size="2xl"/>
			</div>

			{/* eslint-disable i18next/no-literal-string */}
			<Entry version={0}>
				<Card.Body>
					<Card.Title>🎉 Beta version launch</Card.Title>
					<Card.Subtitle>June 2020</Card.Subtitle>
					<p>
						The new app got released! It has a refreshed visual while including all the features present in the previous
						app. Last but not least, there's room for more features to come.
					</p>
				</Card.Body>
			</Entry>
			{/* eslint-enable i18next/no-literal-string */}
		</Screen.Content>
	</Screen>
}

function Entry({ version, children }) {
	//TODO: store this in the local context instead, and then maybe we can keep the version while the user is reading, while we update the state as soon as they visit, so the menu loses the badge but the changelog entries are still highlighted
	const lastVersion = useSelector(getUpdatesVersion)
	return <Card id={`version-${version}`} {...klass({ highlight: version > lastVersion })} children={children}/>
}
Entry.propTypes = {
	version: PropTypes.number.isRequired,
}

function Version({ n }) {
	return <small className="version">{n}</small>
}
Version.propTypes = { n: PropTypes.string.isRequired }

/**
 * Creates an inline element line this: 🧰 Settings > More options > Something else.
 * Makes it standard as italic (as some places quoted them); makes it easier to translate since translations will come from the actual UI elements (pay attention to use the same words - translators and users will thank you); and uses a chevron icon instead of greater-than, so when in Chinese it avoids dotting the symbol.
 * @param parts {string[]|string[][]|object[][]} A list of UI element names; each entry might be a two-part array, with the first being an icon name (or direct props for {@link BtnDesc}) and the second, the UI element name.
 * @returns {JSX.Element}
 */
function NavPath({ parts }) {
	const { t } = useTranslation()
	const pieces = parts.map((part, i) => {
			let piece = part
			if (typeof part != 'string') {
				const icon = (typeof part[0] == 'string')?
					<BtnDesc noText icon={part[0]}/> : //this is used in place of a plain Icon because it's...
					<BtnDesc {...part[0]}/>            //...already tailored to be used in-between text
				piece = <span className="text-nowrap">{icon} {t(part[1])}</span>
			}
			return ((i+1) == parts.length)? [piece] : [piece, <Icon icon="chevron-right" className="px-1 text-muted" size="xs"/>]
		})
		.flat()

	return <I>{pieces}</I>
}
